import { useState } from 'react';
import { Loader2, ThumbsDown, ThumbsUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { PromptRow } from '../types';

interface Props {
  prompt: PromptRow | null;
  mode: 'approve' | 'reject';
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (remarks: string) => void;
}

export function PromptModerationDialog({
  prompt,
  mode,
  isPending,
  onOpenChange,
  onConfirm,
}: Props) {
  const [remarks, setRemarks] = useState('');

  const open = Boolean(prompt);

  const submit = () => {
    onConfirm(remarks.trim());
    setRemarks('');
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setRemarks('');
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'approve' ? (
              <ThumbsUp className="h-4 w-4 text-emerald-600" />
            ) : (
              <ThumbsDown className="h-4 w-4 text-rose-600" />
            )}
            {mode === 'approve' ? 'Approve prompt' : 'Reject prompt'}
          </DialogTitle>
          <DialogDescription>
            {prompt?.fileName ? (
              <>
                File{' '}
                <span className="font-medium text-foreground">
                  {prompt.fileName}
                </span>{' '}
                will be {mode === 'approve' ? 'approved' : 'rejected'}.
              </>
            ) : (
              'Confirm the moderation decision.'
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="remarks">
            Remarks {mode === 'reject' && <span className="text-destructive">*</span>}
          </Label>
          <Textarea
            id="remarks"
            rows={3}
            placeholder={
              mode === 'approve'
                ? 'Optional note for the audit trail'
                : 'Tell the user why this was rejected'
            }
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={
              isPending || (mode === 'reject' && remarks.trim().length === 0)
            }
            variant={mode === 'reject' ? 'destructive' : 'default'}
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
